import {
  AttendeeResponseStatus,
  FilterChipId,
  UserResponseFilterOption,
  ViewMode,
  ViewingStatus,
  ViewingsTableColumnConfig,
} from "./types";

export const VIEWINGS_ROUTE = "/dashboard/crm/viewings";

export const VIEWINGS_VIEW_MODE_QUERY_KEY: Record<ViewMode, string> = {
  list: "list",
  grid: "kanban",
};

export const VIEWING_STATUS_DISPLAY_ORDER: ViewingStatus[] = [
  ViewingStatus.AWAITING_CONFIRMATION,
  ViewingStatus.SCHEDULED,
  ViewingStatus.COMPLETED,
  ViewingStatus.NO_SHOW,
  ViewingStatus.CANCELLED,
];

export interface StatusVisualConfig {
  label: string;
  textColor: string;
  borderColor: string;
  filledBgClass: string;
  outlinedBg: string;
}

export const VIEWING_STATUS_VISUALS: Record<ViewingStatus, StatusVisualConfig> =
  {
    [ViewingStatus.AWAITING_CONFIRMATION]: {
      label: "Awaiting Confirmation",
      textColor: "text-analytics-gray dark:text-analytics-gray-dk",
      borderColor: "border-analytics-gray dark:border-analytics-gray-dk",
      filledBgClass: "bg-analytics-gray/80 dark:bg-analytics-gray-dk/80",
      outlinedBg: "bg-pure-white dark:bg-pure-white-dk",
    },
    [ViewingStatus.SCHEDULED]: {
      label: "Scheduled",
      textColor: "text-tasks-scheduled dark:text-tasks-scheduled-dk",
      borderColor: "border-tasks-scheduled dark:border-tasks-scheduled-dk",
      filledBgClass: "bg-tasks-scheduled/80 dark:bg-tasks-scheduled-dk/80",
      outlinedBg: "bg-pure-white dark:bg-pure-white-dk",
    },
    [ViewingStatus.COMPLETED]: {
      label: "Completed",
      textColor: "text-verba-green-success dark:text-verba-green-success-dk",
      borderColor:
        "border-verba-green-success dark:border-verba-green-success-dk",
      filledBgClass:
        "bg-verba-green-success/80 dark:bg-verba-green-success-dk/80",
      outlinedBg: "bg-pure-white dark:bg-pure-white-dk",
    },
    [ViewingStatus.NO_SHOW]: {
      label: "No Show",
      textColor: "text-lead-stage-engaged dark:text-lead-stage-engaged-dk",
      borderColor:
        "border-lead-stage-engaged dark:border-lead-stage-engaged-dk",
      filledBgClass:
        "bg-lead-stage-engaged/80 dark:bg-lead-stage-engaged-dk/80",
      outlinedBg: "bg-pure-white dark:bg-pure-white-dk",
    },
    [ViewingStatus.CANCELLED]: {
      label: "Cancelled",
      textColor: "text-verba-red-alert dark:text-verba-red-alert-dk",
      borderColor: "border-verba-red-alert dark:border-verba-red-alert-dk",
      filledBgClass: "bg-verba-red-alert/80 dark:bg-verba-red-alert-dk/80",
      outlinedBg: "bg-pure-white dark:bg-pure-white-dk",
    },
  };

export interface AttendeeResponseConfig {
  label: string;
  bgClass: string;
  textClass: string;
}

export const ATTENDEE_RESPONSE_BADGE_CONFIG: Record<
  AttendeeResponseStatus,
  AttendeeResponseConfig
> = {
  accepted: {
    label: "Accepted",
    bgClass: "bg-verba-green-success dark:bg-verba-green-success-dk",
    textClass: "text-pure-white dark:text-pure-white-dk",
  },
  declined: {
    label: "Rejected",
    bgClass: "bg-verba-red-alert dark:bg-verba-red-alert-dk",
    textClass: "text-pure-white dark:text-pure-white-dk",
  },
  tentative: {
    label: "Tentative",
    bgClass: "bg-dark-yellow-for-alerts",
    textClass: "text-verba-indigo dark:text-verba-indigo-dk",
  },
  maybe: {
    label: "Maybe",
    bgClass: "bg-retrying-status dark:bg-retrying-status-dk",
    textClass: "text-pure-white dark:text-pure-white-dk",
  },
};

export interface FilterChipConfig {
  id: FilterChipId;
  label: string;
}

export const VIEWING_FILTER_CHIPS: FilterChipConfig[] = [
  { id: "all", label: "All Viewings" },
  { id: "on-site", label: "On-Site" },
  { id: "virtual", label: "Virtual" },
  { id: "today", label: "Today" },
  { id: "this-week", label: "This Week" },
  { id: "unassigned", label: "Unassigned" },
];

export const VIEWINGS_TABLE_COLUMNS: ViewingsTableColumnConfig[] = [
  { id: "viewing_type", header: "Viewing Type", width: 200 },
  {
    id: "user_response",
    header: "User's Response",
    width: 160,
    sortable: true,
  },
  { id: "lead", header: "Lead Details", width: 160 },
  { id: "date", header: "Date", width: 140 },
  { id: "time", header: "Time", width: 140 },
  {
    id: "preferred_property",
    header: "Preferred Property",
    width: 180,
    sortable: true,
  },
  { id: "preferred_room", header: "Preferred Room", width: 180 },
  { id: "event_name", header: "Event Name", width: 180 },
  { id: "assigned_agents", header: "Assigned Agents", width: 180 },
  { id: "meeting_platform", header: "Meeting Platform", width: 150 },
  { id: "joining_link", header: "Joining Link", width: 160 },
  { id: "notes", header: "Description", width: 180 },
  { id: "created_on", header: "Created On", width: 140 },
  { id: "action_buttons", header: "", width: 260 },
  { id: "actions", header: "", width: 30 },
];

export const VIEWINGS_PAGE_SIZE_OPTIONS = [10, 30, 50, 100];

export const VIEWINGS_DEFAULT_PAGE_SIZE = 10;

export const VIEWINGS_STAGE_PAGE_SIZE = 10;

export const VIEWINGS_STAGE_SHIMMER_COUNT = 3;

export const VIEWINGS_LIST_SHIMMER_ROW_COUNT = 5;

export const VIEWINGS_KANBAN_SHIMMER_CARDS_PER_COLUMN = [3, 3, 2];

export const VIEWINGS_COPY_RESET_MS = 2000;

export const VIEWINGS_CARD_OUTER_CLASS =
  "bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] px-[12px] py-[18px] flex flex-col gap-[18px]";

export const VIEWINGS_CARD_FOOTER_BUTTON_CLASS =
  "flex items-center justify-center gap-[4px] h-[26px] px-[8px] py-[6px] rounded-[6px] border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer";

export const VIEWINGS_CARD_FIELD_VALUE_CLASS =
  "text-md font-medium text-verba-indigo dark:text-verba-indigo-dk";

export const VIEWINGS_CARD_MORE_PILL_CLASS =
  "text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk border border-gray-100 dark:border-gray-100-dk rounded-[4px] px-[4px] py-[2px] whitespace-nowrap";

export const VIEWINGS_POPPER_TOOLTIP_CONTENT_CLASS =
  "bg-popper-bg-fixed-white! dark:bg-popper-bg-fixed-white-dk! text-verba-indigo! dark:text-verba-indigo-dk! shadow-popper-blur-effect rounded-[12px]! p-[14px]!";

export const VIEWINGS_POPPER_TOOLTIP_ARROW_CLASS =
  "fill-popper-bg-fixed-white dark:fill-popper-bg-fixed-white-dk";

export const VIEWINGS_TABLE_ACTION_BUTTON_CLASS =
  "flex items-center justify-center gap-[4px] w-[110px] h-[26px] px-[12px] py-[6px] rounded-[6px] border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk";

export const VIEWINGS_SHIMMER_CLASS =
  "bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse";

export const ASSIGNEES_POPUP_CLOSE_DELAY_MS = 120;
export const ASSIGNEES_POPUP_GAP_PX = 8;
export const ASSIGNEES_POPUP_ARROW_WIDTH_PX = 16;
export const ASSIGNEES_POPUP_ARROW_HALF_WIDTH_PX =
  ASSIGNEES_POPUP_ARROW_WIDTH_PX / 2;
export const ASSIGNEES_POPUP_ARROW_EDGE_PADDING_PX = 12;
export const ASSIGNEES_POPUP_ARROW_LEFT_OFFSET_PX = 44;

export const VIEWINGS_FILTER_POPUP_GAP_PX = 4;
export const USER_RESPONSE_FILTER_WIDTH_PX = 200;

export const USER_RESPONSE_FILTER_OPTIONS: UserResponseFilterOption[] = [
  { id: "accepted", badgeValue: "accepted", values: ["accepted"] },
  { id: "declined", badgeValue: "declined", values: ["declined"] },
  { id: "tentative", badgeValue: "tentative", values: ["tentative"] },
  { id: "maybe", badgeValue: "maybe", values: ["maybe"] },
];

export const PREFERRED_PROPERTY_FILTER_WIDTH_PX = 220;
export const PREFERRED_PROPERTY_SEARCH_DEBOUNCE_MS = 300;
export const NO_PROPERTY_ASSIGNED_KEY = "no_property_assigned";

export const KEBAB_MENU_GAP_PX = 4;
export const KEBAB_MENU_WIDTH_PX = 180;

export const VIEWINGS_FLOATING_HIDE_DELAY_MS = 300;
export const VIEWINGS_FLOATING_NEAR_SCROLLBAR_THRESHOLD_PX = 50;
export const VIEWINGS_FLOATING_OVERLAY_OFFSET_TOP_PX = 18;
export const VIEWINGS_FLOATING_OVERLAY_LEFT_RATIO = 0.45;

export const KEBAB_MENU_ITEM_CLASS =
  "flex items-center gap-[4px] w-full px-[8px] py-[8px] rounded-[8px] text-left hover:bg-hover-effect dark:hover:bg-hover-effect-dk disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer";

export const KEBAB_MENU_ITEM_LABEL_CLASS =
  "text-md font-normal text-heading-text dark:text-heading-text-dk whitespace-nowrap";

export const KEBAB_MENU_ITEM_DELETE_CLASS =
  "flex items-center gap-[4px] w-full px-[8px] py-[8px] rounded-[8px] text-left hover:bg-tag-red-bg dark:hover:bg-tag-red-bg-dk cursor-pointer";

export const KEBAB_MENU_ITEM_DELETE_LABEL_CLASS =
  "text-md font-normal text-verba-red-alert dark:text-verba-red-alert-dk whitespace-nowrap";
